import { ErrorCode, I9nSchemaBySystem } from '@biteinc/common';
import { CardEntryMethod, IntegrationSystem } from '@biteinc/enums';
import { Strings } from '@biteinc/localization';

import type { AuthData } from '~/app/js/gcn_maitred_client';
import { localizeStr, str } from '~/app/js/localization/localization';
import GcnKiosk from '~/app/js/models/gcn_kiosk';
import LocalStorage from '~/app/js/utils/local_storage';
import { GCNCardWithPinView } from '~/app/js/views/gcn_card_with_pin_view';
import { GCNEmailReceiptView } from '~/app/js/views/gcn_email_receipt_view';
import GcnGiftCardBarcodeView from '~/app/js/views/gcn_gift_card_barcode_view';
import { GCNGiftCardTerminalView } from '~/app/js/views/gcn_gift_card_terminal_view';
import StoredValueHelper from '~/helpers/stored_value_helper';

import { useStore } from '../stores';

namespace GcnGiftCardHelper {
  async function getBalance<T extends AuthData>(
    authData: T,
  ): Promise<{ authData: T; balance: number }> {
    switch (gcn.location.getStoredValueSystem()!) {
      case IntegrationSystem.Archer:
        return {
          authData,
          balance: gcn.orderManager.getGrandTotal(),
        };
      case IntegrationSystem.AtriumCampusCard:
      case IntegrationSystem.AtriumStoredValue:
      case IntegrationSystem.AurusPayStoredValue:
      case IntegrationSystem.CbordCampusCard:
      case IntegrationSystem.CbordStoredValue:
      case IntegrationSystem.ChaseStoredValue:
      case IntegrationSystem.EigenStoredValue:
      case IntegrationSystem.FreedomPayKioskStoredValue:
      case IntegrationSystem.FreedomPayStoredValue:
      case IntegrationSystem.Givex:
      case IntegrationSystem.OloStoredValue:
      case IntegrationSystem.ParPayStoredValue:
      case IntegrationSystem.PaytronixGift:
      case IntegrationSystem.PinataPayNativeStoredValue:
      case IntegrationSystem.PinataPayStoredValue:
      case IntegrationSystem.PinataStoredValue:
      case IntegrationSystem.QuickchargeCampusCard:
      case IntegrationSystem.StarbucksStoredValue:
      case IntegrationSystem.Tcc:
      case IntegrationSystem.Valutec:
      case IntegrationSystem.WorldpayStoredValue: {
        return gcn.maitred.giftCardInquire(authData);
      }
    }
  }

  export async function captureMaximumAmount(authData: AuthData): Promise<void> {
    gcn.menuView.showSpinner(localizeStr(Strings.LOOKUP_CARD));

    let inquireData;
    try {
      inquireData = await getBalance(authData);
    } finally {
      gcn.menuView.dismissSpinner();
    }

    const { authData: updatedAuthData, balance } = inquireData;
    const balanceLimitsSpend = StoredValueHelper.balanceLimitsStoredValueOrCampusCardSpend(
      gcn.location.get('giftCardI9n'),
      gcn.location.get('campusCardI9n'),
      updatedAuthData.accountType,
      updatedAuthData.cardSchemeId,
    );
    const cardNumber = updatedAuthData.cardNumber ?? authData.cardNumber;
    if (cardNumber) {
      gcn.orderManager.setBalanceForStoredValueCard(
        cardNumber,
        balanceLimitsSpend ? balance : undefined,
      );
    }

    if (balance <= 0 && balanceLimitsSpend) {
      const err = new Error(`0 ${str(Strings.BALANCE)}`);
      // @ts-expect-error we haven't defined a code on our errors
      err.code = ErrorCode.StoredValueCardInsufficientFunds;
      throw err;
    }

    const defaultAmount = balanceLimitsSpend
      ? Math.min(gcn.orderManager.getGrandTotal(), balance)
      : gcn.orderManager.getGrandTotal();
    gcn.menuView.showSpinner(localizeStr(Strings.PROCESSING_PAYMENT));

    let data;
    try {
      data = await gcn.maitred.giftCardCapture(updatedAuthData, defaultAmount);
    } finally {
      gcn.menuView.dismissSpinner();
    }

    gcn.orderManager.setOrderFromJSON(data.order);
    gcn.orderManager.setTransactionsFromJson(data.successfulTransactions);
    useStore.getState().checkout.onStoredValueUpdated();
  }

  export function ensureGuestEmail(callback: Function): void {
    if (gcn.orderManager.getGuestEmail()) {
      callback();
      return;
    }
    const storedGuestEmail = LocalStorage.getItem('guestEmail');
    // Payment by token requires an email because it's generally shared with eComm.
    gcn.menuView.showModalPopup(
      new GCNEmailReceiptView({
        callback: () => {
          callback();
        },
        setEmailOnly: true,
        showRequiredMessage: true,
        ...(storedGuestEmail && { email: storedGuestEmail }),
        ...(window.prefilledGuestEmail && { email: window.prefilledGuestEmail }),
      }),
    );
  }

  function storedValueUsesBarcode(): boolean {
    const storedValueIntegration = gcn.location.getStoredValueIntegration();
    if (!storedValueIntegration) {
      return false;
    }

    const storedValueSchema = I9nSchemaBySystem[storedValueIntegration.system];
    if (!('supportsBarcode' in storedValueSchema) || !storedValueSchema.supportsBarcode) {
      return false;
    }
    // Obviously can't scan anything on the web
    if (window.isFlash) {
      return false;
    }
    // We need a scanner or at least be in preview
    if (!GcnKiosk.isScannerConnected() && !window.isKioskPreview) {
      return false;
    }
    switch (storedValueIntegration.system) {
      case IntegrationSystem.OloStoredValue:
      case IntegrationSystem.PaytronixGift:
      case IntegrationSystem.PinataStoredValue:
        return !!storedValueIntegration.hasBarcodes;
      default:
        return true;
    }
  }

  export function showGiftCardView(
    mode: 'comp-card' | 'stored-value',
    callback: (queuePath?: string) => void,
  ): void {
    // Tender-expanded cards were fetched at SSO time, before any order existed. Now that
    // there is a cart, re-fetch so the server can filter tenders by it (e.g. only offer
    // the meal-exchange tender when its item is in the cart). Cards that already paid
    // are kept so their applied payment rows stay labeled.
    // Atrium campus-card session cards (BYU-style: stored value seeded by an external
    // portal's session token, no tenderId yet) expand into tenders the same way — the
    // server resolves the session token to the card number.
    const unusedTenderCards = gcn.orderManager.getUnusedStoredValueCards().filter((card) => {
      if (!card.authSessionToken) {
        return false;
      }
      return (
        !!card.tenderId ||
        gcn.location.getStoredValueSystem() === IntegrationSystem.AtriumCampusCard
      );
    });
    if (mode === 'stored-value' && unusedTenderCards.length && gcn.orderManager.getOrderId()) {
      // Cards can come from more than one session token (e.g. two portal logins).
      // Refresh each token and merge, so no card disappears with the pre-order list.
      const sessionTokens = [...new Set(unusedTenderCards.map((card) => card.authSessionToken!))];
      const refreshedCards: typeof unusedTenderCards = [];
      let pendingTokenCount = sessionTokens.length;
      const finishToken = (): void => {
        pendingTokenCount--;
        if (pendingTokenCount > 0) {
          return;
        }
        const usedCards = gcn.orderManager.getStoredValueCards().filter((card) => {
          return !gcn.orderManager
            .getUnusedStoredValueCards()
            .some((unusedCard) => unusedCard === card);
        });
        gcn.orderManager.setStoredValueCards([
          ...usedCards,
          ...refreshedCards.filter((refreshedCard) => {
            return !usedCards.some((usedCard) => usedCard.tenderId === refreshedCard.tenderId);
          }),
        ]);
        showGiftCardViewNow(mode, callback);
      };
      sessionTokens.forEach((sessionToken) => {
        gcn.maitred.storedValueGetTenders(
          sessionToken,
          (
            err: unknown,
            data?: {
              tenders?: {
                accountDisplayName: string;
                accountTender: string;
                accountType?: number;
                balance?: number;
              }[];
            },
          ) => {
            if (!err && data?.tenders) {
              data.tenders.forEach((account) => {
                refreshedCards.push({
                  cardName: account.accountDisplayName,
                  lastFour: StoredValueHelper.lastFourFromToken(sessionToken),
                  authSessionToken: sessionToken,
                  cardEntryMethod: CardEntryMethod.CardOnFile,
                  tenderId: account.accountTender,
                  accountType: account.accountType,
                  balance: account.balance,
                });
              });
            } else {
              // Keep this token's pre-refresh cards instead of dropping them.
              refreshedCards.push(
                ...unusedTenderCards.filter((card) => card.authSessionToken === sessionToken),
              );
            }
            finishToken();
          },
        );
      });
      return;
    }
    showGiftCardViewNow(mode, callback);
  }

  function showGiftCardViewNow(
    mode: 'comp-card' | 'stored-value',
    callback: (queuePath?: string) => void,
  ): void {
    if (mode === 'stored-value' && storedValueUsesBarcode()) {
      const giftCardBarcodeView = new GcnGiftCardBarcodeView({
        callback: (queuePath?: string) => {
          gcn.menuView.dismissStablePopup();
          callback(queuePath);
        },
      });
      gcn.menuView.showStablePopup(giftCardBarcodeView, 'gift-card-barcode-view');
    } else {
      if (
        gcn.kiosk?.get('hasMsr') &&
        gcn.location.get('compCardI9n') === IntegrationSystem.PaytronixCompCard
      ) {
        const giftCardTerminalView = new GCNGiftCardTerminalView(mode, {
          grandTotal: gcn.orderManager.getGrandTotal(),
          callback: (queuePath?: string) => {
            gcn.menuView.dismissStablePopup();
            callback(queuePath);
          },
        });
        gcn.menuView.showStablePopup(giftCardTerminalView, 'gift-card-terminal-view');
      } else {
        const cardWithPinView = new GCNCardWithPinView(mode, {
          callback: (queuePath?: string) => {
            gcn.menuView.dismissStablePopup();
            callback(queuePath);
          },
        });
        gcn.menuView.showStablePopup(cardWithPinView, 'gift-card-view');
      }
    }
  }

  export function showGiftCardViewWithPrefilledCardNumber(
    cardNumber: string,
    callback: Function,
  ): void {
    const giftCardView = new GCNCardWithPinView('stored-value', {
      cardNumber,
      callback: () => {
        gcn.menuView.dismissStablePopup();
        callback();
      },
    });
    gcn.menuView.showStablePopup(giftCardView, 'gift-card-view');
  }
}

export default GcnGiftCardHelper;
